/**
 * Text sizes, and stepping between them.
 *
 * Its own module rather than living in the toolbar, because the toolbar
 * and the keyboard shortcut have to agree about what "one size bigger"
 * means — and a copy in each is two definitions that drift the first
 * time somebody adds a size to one of them.
 */

// The sizes these charts are actually drawn in: 8 for a deed block, 10
// for a party, and up from there for the notes an examiner writes across
// a whole section.
export const SIZES = [6, 8, 9, 10, 12, 14, 18, 24, 36, 48, 72];

// Wide, because a note across a section chart is read at 16% zoom and a
// party box is read at 200%.
export const MIN_FONT = 6;
export const MAX_FONT = 200;

export const clampFont = (value) => Math.max(
  MIN_FONT,
  Math.min(MAX_FONT, Math.round(Number(value)) || MIN_FONT),
);

/**
 * The next size up or down.
 *
 * Follows the list where it can, so stepping moves between the sizes
 * people actually pick rather than one point at a time — and falls back
 * to a ratio past the ends of it, so a 90pt heading can still grow.
 */
export function stepFont(size, direction) {
  const now = Number(size) || 10;
  if (direction > 0) {
    const bigger = SIZES.find((s) => s > now);
    return clampFont(bigger ?? now * 1.25);
  }
  const smaller = [...SIZES].reverse().find((s) => s < now);
  return clampFont(smaller ?? now / 1.25);
}

/**
 * The nearest size on the ladder.
 *
 * For resizing a text box by its handles, where the arithmetic gives
 * 13.7 and nobody wants a chart drawn in 13.7. The ladder is the same
 * one the toolbar and the keyboard step through, so a box dragged to a
 * size and a box typed to a size are the same size — which is the whole
 * point, since a chain is read as columns and two headings a point apart
 * look like a mistake.
 *
 * Past the top of the ladder it rounds to whole points: a 90pt heading
 * across a section is somebody being deliberate, and there is no list of
 * usual sizes up there to pull it to.
 */
export function nearestFont(value) {
  const want = Number(value);
  if (!Number.isFinite(want)) return MIN_FONT;
  if (want > SIZES[SIZES.length - 1]) return clampFont(want);
  let best = SIZES[0];
  for (const size of SIZES) {
    if (Math.abs(size - want) < Math.abs(best - want)) best = size;
  }
  return clampFont(best);
}
